import { createFileRoute, Link } from "@tanstack/react-router";
import { useSession } from "@tanstack/react-start/server";
import { ShieldCheck, Store, Database, Globe2, Lock } from "lucide-react";
import { betaSessionConfig, type BetaSession } from "@/lib/beta-session";
import { NexKeyLogo } from "@/components/nexkey-logo";

function safeRedirect(input: unknown): string {
  if (typeof input !== "string") return "/";
  if (!input.startsWith("/") || input.startsWith("//")) return "/";
  if (input.startsWith("/login") || input.startsWith("/logout")) return "/";
  return input;
}

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Private Beta Access — NexKey.gg" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "NexKey.gg private beta — authorized partners and testers only." },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: typeof s.redirect === "string" ? s.redirect : "/",
    error: s.error === "1" || s.error === 1 ? true : false,
  }),
  server: {
    handlers: {
      POST: async ({ request }) => {
        const form = await request.formData();
        const username = String(form.get("username") ?? "").trim();
        const password = String(form.get("password") ?? "");
        const redirectTo = safeRedirect(form.get("redirect"));

        const expectedUser = process.env.BETA_USERNAME;
        const expectedPass = process.env.BETA_PASSWORD;
        if (!expectedUser || !expectedPass) {
          return new Response("Beta credentials are not configured", { status: 500 });
        }

        if (username !== expectedUser || password !== expectedPass) {
          return new Response(null, {
            status: 302,
            headers: {
              Location: `/login?error=1&redirect=${encodeURIComponent(redirectTo)}`,
              "cache-control": "no-store",
            },
          });
        }

        const session = await useSession<BetaSession>(betaSessionConfig());
        await session.update({ unlocked: true });

        return new Response(null, {
          status: 302,
          headers: { Location: redirectTo, "cache-control": "no-store" },
        });
      },
    },
  },
  component: LoginPage,
});

const FEATURES = [
  { icon: Store, label: "Compare game prices" },
  { icon: ShieldCheck, label: "Official partner stores" },
  { icon: Database, label: "Steam-powered database" },
  { icon: Globe2, label: "Multi-language & multi-currency" },
];

function LoginPage() {
  const { redirect, error } = Route.useSearch();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,theme(colors.primary/0.25),transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 h-[60vh] bg-[radial-gradient(ellipse_at_bottom,theme(colors.accent/0.25),transparent_65%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
      </div>

      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center px-6 py-12 lg:grid lg:grid-cols-2 lg:gap-16">
        {/* Left: brand + copy */}
        <div className="flex flex-col justify-center">
          <Link to="/login" search={{ redirect, error: false }} aria-label="NexKey.gg">
            <NexKeyLogo className="text-3xl font-extrabold" markClassName="h-14 w-14" uppercase eager />
          </Link>

          <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
            <Lock className="h-3 w-3" /> Private Beta
          </span>

          <h1 className="mt-6 font-display text-4xl font-bold leading-tight sm:text-5xl">
            Private Beta <span className="text-gradient">Access</span>
          </h1>
          <p className="mt-4 max-w-md text-sm text-muted-foreground sm:text-base">
            NexKey is currently in closed beta and available only to authorized
            partners and testers.
          </p>

          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <li
                key={f.label}
                className="flex items-center gap-3 rounded-xl border border-border/60 bg-surface/40 px-3 py-2.5 text-sm backdrop-blur-sm"
              >
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary">
                  <f.icon className="h-4 w-4" />
                </span>
                <span className="text-foreground/90">{f.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: glass card */}
        <div className="mt-12 flex items-center justify-center lg:mt-0">
          <div className="w-full max-w-md">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 shadow-glow backdrop-blur-xl">
              <div className="absolute inset-x-8 -top-px h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

              <div className="mb-6">
                <h2 className="font-display text-xl font-semibold">Sign in to continue</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Use the credentials provided by the NexKey team.
                </p>
              </div>

              <form method="POST" action="/login" className="space-y-4">
                <input type="hidden" name="redirect" value={redirect} />

                <label className="block">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Username
                  </span>
                  <input
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    autoFocus
                    className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/60 focus:border-primary/70 focus:ring-2 focus:ring-primary/30"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Password
                  </span>
                  <input
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="mt-1.5 w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/60 focus:border-primary/70 focus:ring-2 focus:ring-primary/30"
                  />
                </label>

                {error && (
                  <p className="rounded-md border border-destructive/40 bg-destructive/15 px-3 py-2 text-xs text-destructive-foreground">
                    Invalid username or password.
                  </p>
                )}

                <button
                  type="submit"
                  className="group relative mt-2 w-full overflow-hidden rounded-lg bg-primary-gradient px-4 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition hover:brightness-110"
                >
                  Access NexKey
                </button>
              </form>

              <div className="mt-6 border-t border-white/10 pt-4 text-center text-xs text-muted-foreground">
                <p>Need access?</p>
                <a
                  href="mailto:info@nexkey.gg"
                  className="mt-1 inline-block font-medium text-primary hover:underline"
                >
                  info@nexkey.gg
                </a>
              </div>
            </div>

            <p className="mt-4 text-center text-[11px] text-muted-foreground">
              Protected area — access is logged and rate-limited.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
